import React, { useState } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import AppLayout, { PageHeader, useMenu, legacyPath } from "./layout/AppLayout";
import Login from "./pages/Login";
import LandingPage from "./pages/LandingPage";
import { ValidityExpired, ValidityWarning } from "./pages/Validity";
import CreateBooking from "./modules/purchase/CreateBooking";
import BookingList from "./modules/purchase/BookingList";
import SupplierList from "./modules/purchase/SupplierList";
import CreateArrival from "./modules/purchase/CreateArrival";
import ArrivalList from "./modules/purchase/ArrivalList";
import LotsList from "./modules/purchase/LotsList";
import LotTrack from "./modules/purchase/LotTrack";
import BillsList from "./modules/purchase/BillsList";
import BillView from "./modules/purchase/BillView";
import Payments from "./modules/purchase/Payments";
import PaymentSummary from "./modules/purchase/PaymentSummary";
import PaymentVoucher from "./modules/purchase/PaymentVoucher";
import { AgentList, FinancierList, VehicleList } from "./modules/purchase/Masters";
import PurchaseRates from "./modules/purchase/PurchaseRates";
import RmAnalysisList from "./modules/qc/RmAnalysisList";
import OrganicCheckCreate from "./modules/qc/OrganicCheckCreate";
import AnalysisPreview from "./modules/qc/AnalysisPreview";
import AbReportsList from "./modules/qc/AbReportsList";
import AbReportDetail from "./modules/qc/AbReportDetail";
import { AnalysisRequiredMaster, LabNamesMaster } from "./modules/qc/Masters";
import TraceabilityList from "./modules/qc/TraceabilityList";
import TracksList from "./modules/preprocessing/TracksList";
import ReceivalPage from "./modules/preprocessing/ReceivalPage";
import DeheadingPage from "./modules/preprocessing/DeheadingPage";
import GradingPage from "./modules/preprocessing/GradingPage";
import ValueAdditionPage from "./modules/preprocessing/ValueAdditionPage";
import { PpcStationsMaster, HlVaVarietyMaster } from "./modules/preprocessing/Masters";
import SoakingPage from "./modules/production/SoakingPage";
import FreezingPage from "./modules/production/FreezingPage";
import ProductionDataPage from "./modules/coldstore/ProductionDataPage";
import InwardPage from "./modules/coldstore/InwardPage";
import InwardApprovalPage from "./modules/coldstore/InwardApprovalPage";
import StockSearchPage from "./modules/coldstore/StockSearchPage";
import StockOutPage from "./modules/coldstore/StockOutPage";
import { ColdstoresMaster, TenantsMaster, BlocksMaster, FloorsMaster } from "./modules/coldstore/Masters";
import IbtTransferPage from "./modules/coldstore/IbtTransferPage";
import { UsersPage, AccessRolesPage } from "./modules/admin/UsersRolesPage";
import PiListPage from "./modules/sales/PiListPage";
import { BuyersMaster, ConsigneesMaster, BanksMaster } from "./modules/sales/Masters";
import ClearingAgentPage from "./modules/sales/ClearingAgentPage";
import { api, errorText } from "./api/client";
import { Alert } from "./components/erp";
import { Link } from "react-router-dom";

const RequireAuth: React.FC = () => {
  const { token, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="p-4"><span className="erp-spinner" /> Loading session…</div>;
  if (!token) return <Navigate to="/auth-login" replace state={{ from: loc }} />;
  return <Outlet />;
};

/** Landing after login (legacy redirected to $_SESSION["ClientPath"]; menu is the navigation source) */
const Home: React.FC = () => {
  const { session } = useAuth();
  const menu = useMenu();
  return (
    <>
      <PageHeader title={session?.ClientName || "Dashboard"} location="Home" />
      <div className="card"><div className="card-body" data-testid="home-page">
        <p className="mb-2">Welcome, <strong>{session?.UserName}</strong>. Select a module from the top menu.</p>
        {menu.isError && <Alert kind="danger">Menu unavailable: {errorText(menu.error)}</Alert>}
        <div className="row">
          {(menu.data?.Data || []).map((m) => (
            <div className="col-md-3 mb-3" key={m.MenuName}>
              <h6 className="mb-1"><i className={`${m.MenuIcon} me-1`} />{m.MenuName}</h6>
              <ul className="list-unstyled mb-0" style={{ fontSize: 13 }}>{m.SubMenu.map((s) => <li key={`${s.ID}-${s.PathUrl}`}><Link to={legacyPath(s.PathUrl)}>{s.Name}</Link></li>)}</ul>
            </div>
          ))}
        </div>
      </div></div>
    </>
  );
};

const NotMigrated: React.FC = () => {
  const loc = useLocation();
  return (
    <>
      <PageHeader title="Screen not migrated yet" />
      <div className="card"><div className="card-body" data-testid="not-migrated">
        <p className="mb-1">Legacy screen <code>{loc.pathname.replace(/^\/v3\//, "v3/")}.php</code> is not yet available in the new UI.</p>
        <p className="text-muted mb-0">See MIGRATION.md for status.</p>
      </div></div>
    </>
  );
};

/** Port of ChangePassword.php */
const ChangePassword: React.FC = () => {
  const [p, setP] = useState(""); const [c, setC] = useState(""); const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const { logout } = useAuth(); const nav = useNavigate();
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const r = (await api.post("/auth/change-password", { userpassword: p, cnfpassword: c })).data;
      if (r.Error) setMsg({ ok: false, text: r.Error }); else { setMsg({ ok: true, text: r.Msg }); setTimeout(() => { logout(); nav("/auth-login"); }, 1200); }
    } catch (err) { setMsg({ ok: false, text: errorText(err) }); }
  };
  return (
    <>
      <PageHeader title="Change Password" location="Account" />
      <div className="card" style={{ maxWidth: 480 }}><div className="card-body">
        <form onSubmit={submit} data-testid="change-password-form">
          <div className="mb-3"><label className="form-label" htmlFor="userpassword">Current Password</label><input required type="password" id="userpassword" className="form-control" value={p} onChange={(e) => setP(e.target.value)} data-testid="current-password" /></div>
          <div className="mb-3"><label className="form-label" htmlFor="cnfpassword">New Password</label><input required type="password" id="cnfpassword" className="form-control" value={c} onChange={(e) => setC(e.target.value)} data-testid="new-password" /></div>
          {msg && <Alert kind={msg.ok ? "success" : "danger"} testId="change-password-msg">{msg.text}</Alert>}
          <button className="btn btn-primary" type="submit" data-testid="change-password-submit">Change Password</button>
        </form>
      </div></div>
    </>
  );
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/auth-login" element={<Login />} />
          <Route path="/login" element={<Navigate to="/auth-login" replace />} />
          <Route path="/ValidityWarning" element={<ValidityWarning />} />
          <Route path="/includes/Validate" element={<ValidityExpired />} />
          <Route element={<RequireAuth />}>
            <Route element={<AppLayout />}>
              <Route path="/index" element={<Home />} />
              <Route path="/ChangePassword" element={<ChangePassword />} />
              {/* Purchase */}
              <Route path="/v3/Purchase/create-booking" element={<CreateBooking />} />
              <Route path="/v3/Purchase/rm-booking-list" element={<BookingList />} />
              <Route path="/v3/Purchase/supplier-list" element={<SupplierList />} />
              <Route path="/v3/Purchase/create-arrival" element={<CreateArrival />} />
              <Route path="/v3/Purchase/rm-arrival-list" element={<ArrivalList />} />
              <Route path="/v3/Purchase/LotsList" element={<LotsList />} />
              <Route path="/v3/Purchase/rm-bills-list" element={<BillsList />} />
              <Route path="/v3/Purchase/rm-bill" element={<BillView />} />
              <Route path="/v3/Purchase/BillPreview" element={<BillView />} />
              <Route path="/v3/Purchase/rm-lots-track" element={<LotTrack />} />
              <Route path="/v3/Purchase/rm-payments" element={<Payments />} />
              <Route path="/v3/Purchase/rm-payment-summary" element={<PaymentSummary />} />
              <Route path="/v3/Purchase/rm-payment-voucher" element={<PaymentVoucher />} />
              <Route path="/v3/Purchase/rm-agent-list" element={<AgentList />} />
              <Route path="/v3/Purchase/rm-financier-list" element={<FinancierList />} />
              <Route path="/v3/Purchase/VehicleList" element={<VehicleList />} />
              <Route path="/v3/Purchase/AddPurchaseRates" element={<PurchaseRates />} />
              {/* Quality Control */}
              <Route path="/v3/QualityControl/RmAnalysis" element={<RmAnalysisList />} />
              <Route path="/v3/QualityControl/RmReportCreate" element={<OrganicCheckCreate />} />
              <Route path="/v3/QualityControl/RmAnalysisPreview" element={<AnalysisPreview />} />
              <Route path="/v3/QualityControl/DailyLabReports" element={<AbReportsList />} />
              <Route path="/v3/QualityControl/RmAbReport" element={<AbReportDetail />} />
              <Route path="/v3/QualityControl/lab-names-list" element={<LabNamesMaster />} />
              <Route path="/v3/QualityControl/analysis-required-list" element={<AnalysisRequiredMaster />} />
              <Route path="/v3/QualityControl/TraceabilityList" element={<TraceabilityList />} />
              {/* Preprocessing */}
              <Route path="/v3/PreProcessing/lot-tracking" element={<TracksList />} />
              <Route path="/v3/PreProcessing/receival-page-trackid" element={<ReceivalPage />} />
              <Route path="/v3/PreProcessing/de-heading-trackid" element={<DeheadingPage />} />
              <Route path="/v3/PreProcessing/grading-trackid" element={<GradingPage />} />
              <Route path="/v3/PreProcessing/value-addition-trackid" element={<ValueAdditionPage />} />
              <Route path="/v3/PreProcessing/ppc-stations-list" element={<PpcStationsMaster />} />
              <Route path="/v3/PreProcessing/hlva-variety-list" element={<HlVaVarietyMaster />} />
              {/* Production */}
              <Route path="/v3/Production/soaking-trackid" element={<SoakingPage />} />
              <Route path="/v3/Production/freezing-trackid" element={<FreezingPage />} />
              <Route path="/v3/ColdStore/daily-production-data" element={<ProductionDataPage />} />
              <Route path="/v3/ColdStore/daily-inward" element={<InwardPage />} />
              <Route path="/v3/ColdStore/admin-inward-approval" element={<InwardApprovalPage />} />
              <Route path="/v3/ColdStore/store-stock-search" element={<StockSearchPage />} />
              <Route path="/v3/ColdStore/stock-out" element={<StockOutPage />} />
              <Route path="/v3/ColdStore/coldstores-list" element={<ColdstoresMaster />} />
              <Route path="/v3/ColdStore/tenants-list" element={<TenantsMaster />} />
              <Route path="/v3/ColdStore/blocks-list" element={<BlocksMaster />} />
              <Route path="/v3/ColdStore/floors-list" element={<FloorsMaster />} />
              <Route path="/v3/ColdStore/ibt-transfer" element={<IbtTransferPage />} />
              <Route path="/v3/Admin/users-list" element={<UsersPage />} />
              <Route path="/v3/Admin/edit-roles" element={<AccessRolesPage />} />
              <Route path="/v3/Sales/pi-list" element={<PiListPage />} />
              <Route path="/v3/Sales/buyers" element={<BuyersMaster />} />
              <Route path="/v3/Sales/consignees" element={<ConsigneesMaster />} />
              <Route path="/v3/Sales/banks" element={<BanksMaster />} />
              <Route path="/v3/Sales/clearing-agent-status" element={<ClearingAgentPage />} />
              {/* Module-root redirects: clicking a primary-nav pill hits the bare module path;
                  route it to the first real migrated screen instead of the generic catch-all. */}
              <Route path="/v3/Sales" element={<Navigate to="/v3/Sales/pi-list" replace />} />
              <Route path="/v3/Purchase" element={<Navigate to="/v3/Purchase/rm-booking-list" replace />} />
              <Route path="/v3/QualityControl" element={<Navigate to="/v3/QualityControl/RmAnalysis" replace />} />
              <Route path="/v3/PreProcessing" element={<Navigate to="/v3/PreProcessing/lot-tracking" replace />} />
              <Route path="/v3/Production" element={<Navigate to="/v3/Production/soaking-trackid" replace />} />
              <Route path="/v3/ColdStore" element={<Navigate to="/v3/ColdStore/daily-production-data" replace />} />
              <Route path="/v3/Admin" element={<Navigate to="/v3/Admin/users-list" replace />} />
              <Route path="/v3/*" element={<NotMigrated />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
